/**
 * #939 3b — the Lodging Calendar's range arithmetic (pure, no React, no I/O).
 * Day / week / month ranges around an anchor date, in YYYY-MM-DD, with no
 * timezone arithmetic (the server owns the semantics; this only picks the
 * columns). Weeks run Monday → Sunday, like the schedule.
 */
export type LodgingView = 'day' | 'week' | 'month';

export const LODGING_VIEWS: ReadonlyArray<{ key: LodgingView; label: string }> = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
];

export function parseLodgingView(raw: string | null | undefined): LodgingView {
  return raw === 'week' || raw === 'month' ? raw : 'day';
}

export function shiftIsoDate(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

function utc(iso: string): Date {
  return new Date(`${iso}T00:00:00.000Z`);
}

/** Monday of the week holding `iso`. */
export function weekStart(iso: string): string {
  const d = utc(iso);
  const dow = d.getUTCDay(); // 0 = Sunday
  const back = (dow + 6) % 7;
  return shiftIsoDate(iso, -back);
}

export function monthStart(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}

export function monthEnd(iso: string): string {
  const [y, m] = iso.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${iso.slice(0, 7)}-${String(last).padStart(2, '0')}`;
}

export function rangeFor(view: LodgingView, anchor: string): { start: string; end: string } {
  if (view === 'day') return { start: anchor, end: anchor };
  if (view === 'week') {
    const start = weekStart(anchor);
    return { start, end: shiftIsoDate(start, 6) };
  }
  return { start: monthStart(anchor), end: monthEnd(anchor) };
}

/** The anchor one step forward / back in the current view. */
export function stepAnchor(view: LodgingView, anchor: string, direction: 1 | -1): string {
  if (view === 'day') return shiftIsoDate(anchor, direction);
  if (view === 'week') return shiftIsoDate(anchor, 7 * direction);
  const [y, m] = anchor.split('-').map(Number) as [number, number];
  const next = new Date(Date.UTC(y, m - 1 + direction, 1));
  return next.toISOString().slice(0, 10);
}

export function isoDays(start: string, end: string): string[] {
  const out: string[] = [];
  for (let d = start; d <= end; d = shiftIsoDate(d, 1)) out.push(d);
  return out;
}

/** "Mon 15" — column heads. */
export function formatDayHead(iso: string): { weekday: string; day: string } {
  const d = new Date(`${iso}T12:00:00`);
  return {
    weekday: d.toLocaleDateString('en-US', { weekday: 'short' }),
    day: String(d.getDate()),
  };
}

/** "Mon, Sep 15" / "Sep 15 – 21, 2026" / "September 2026" */
export function formatRangeLabel(view: LodgingView, start: string, end: string): string {
  const s = new Date(`${start}T12:00:00`);
  const e = new Date(`${end}T12:00:00`);
  if (view === 'day') return s.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  if (view === 'month') return s.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const sameMonth = s.getMonth() === e.getMonth();
  const left = s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const right = e.toLocaleDateString('en-US', sameMonth ? { day: 'numeric' } : { month: 'short', day: 'numeric' });
  return `${left} – ${right}, ${e.getFullYear()}`;
}

/** "Sep 15 → Sep 18" */
export function formatSpan(startIso: string, endIso: string): string {
  const f = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${f(startIso)} → ${f(endIso)}`;
}

export function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}
